import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { notification } from '@antoree/ant-ui';
import { videoStorage } from '../utils/videoStorage';
import { API_SERVICE_URL } from 'configs/env.conf';
import { useIntl } from 'react-intl';
import { useCurrentUser } from 'contexts';

export type RecordingStatus = 'idle' | 'recording';

export interface RecordedVideo {
  blob: Blob;
  url: string;
  sessionId: string;
  courseId: string;
  sessionTitle: string;
  timestamp: number;
  id?: string;
  uploadStatus?: 'pending' | 'uploading' | 'success' | 'failed';
}

interface ScreenRecorderContextType {
  recordingStatus: RecordingStatus;
  recordedVideo: RecordedVideo | null;
  startRecording: (
    sessionId: string,
    courseId: string,
    subject: string,
    title: string,
  ) => Promise<void>;
  stopRecording: () => void;
  requestStopRecording: () => void;
  cancelStopRecording: () => void;
  isConfirmStopOpen: boolean;
  isShowStartWarningModal: boolean;
  closeStartWarningModal: () => void;
  setRecordedVideo: (video: RecordedVideo | null) => void;
  activeSessionId: string | null;
  setAiTip: (tip: string | null) => void;
}

const ScreenRecorderContext = createContext<
  ScreenRecorderContextType | undefined
>(undefined);

export const ScreenRecorderProvider: React.FC = ({ children }) => {
  const intl = useIntl();
  const { id: userId } = useCurrentUser();
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>(
    'idle',
  );
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [recordedVideo, setRecordedVideo] = useState<RecordedVideo | null>(
    null,
  );
  const [isConfirmStopOpen, setIsConfirmStopOpen] = useState<boolean>(false);
  const [isShowStartWarningModal, setIsShowStartWarningModal] = useState<boolean>(false);

  const closeStartWarningModal = useCallback(() => {
    setIsShowStartWarningModal(false);
  }, []);

  const requestStopRecording = useCallback(() => {
    if (recordingStatusRef.current === 'recording') {
      setIsConfirmStopOpen(true);
    }
  }, []);

  const cancelStopRecording = useCallback(() => {
    setIsConfirmStopOpen(false);
  }, []);

  const mediaRecorderRef = useRef<any | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const finalStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sessionInfoRef = useRef<{
    sessionId: string;
    courseId: string;
    title: string;
  } | null>(null);
  const mimeTypeRef = useRef<string>('video/webm');
  const isStartingRef = useRef<boolean>(false);
  const recordingStatusRef = useRef<RecordingStatus>('idle');
  const activeSessionIdRef = useRef<string | null>(null);
  const recordingStartTimeRef = useRef<number>(0);

  /**
   * Fix WebM duration metadata so the recorded file is seekable and shows
   * correct duration when downloaded. Implemented inline to avoid extra deps.
   *
   * Strategy: locate the Duration element in the EBML Segment Info block and
   * write the actual duration (in ms, as a 64-bit IEEE 754 float) into it.
   * If the header already has a placeholder (written as 0 or NaN) we overwrite
   * it; otherwise we fall back to returning the original blob.
   */
  const fixWebmDuration = useCallback(
    async (blob: Blob, durationMs: number): Promise<Blob> => {
      try {
        const buffer = await blob.arrayBuffer();
        const data = new Uint8Array(buffer);

        // EBML element IDs we need to find
        const SEGMENT_INFO_ID = [0x15, 0x49, 0xa9, 0x66];
        const DURATION_ID = [0x44, 0x89];

        const matchBytes = (offset: number, pattern: number[]) => {
          for (let i = 0; i < pattern.length; i++) {
            if (data[offset + i] !== pattern[i]) return false;
          }
          return true;
        };

        // Read variable-length EBML size
        const readVint = (
          offset: number,
        ): { value: number; bytesRead: number } => {
          const firstByte = data[offset];
          if (firstByte === 0) return { value: 0, bytesRead: 1 };
          let mask = 0x80;
          let bytesRead = 1;
          while (!(firstByte & mask) && bytesRead <= 8) {
            mask >>= 1;
            bytesRead++;
          }
          let value = firstByte & (mask - 1);
          for (let i = 1; i < bytesRead; i++) {
            value = (value << 8) | data[offset + i];
          }
          return { value, bytesRead };
        };

        // Search for SegmentInfo (0x1549a966)
        let segInfoOffset = -1;
        for (let i = 0; i < data.length - 4; i++) {
          if (matchBytes(i, SEGMENT_INFO_ID)) {
            segInfoOffset = i;
            break;
          }
        }
        if (segInfoOffset === -1) return blob;

        // Skip SegmentInfo element ID (4 bytes) + its vint size
        const afterSegId = segInfoOffset + 4;
        const segSizeInfo = readVint(afterSegId);
        const segInfoContentStart = afterSegId + segSizeInfo.bytesRead;

        // Search for Duration element (0x4489) within SegmentInfo
        let durationOffset = -1;
        const segInfoEnd = segInfoContentStart + segSizeInfo.value;
        for (
          let i = segInfoContentStart;
          i < Math.min(segInfoEnd, data.length - 2);
          i++
        ) {
          if (matchBytes(i, DURATION_ID)) {
            durationOffset = i;
            break;
          }
        }
        if (durationOffset === -1) return blob;

        // Duration element: 2-byte ID + vint size + float64 value
        const durSizeInfo = readVint(durationOffset + 2);
        const durValueOffset = durationOffset + 2 + durSizeInfo.bytesRead;

        if (durSizeInfo.value !== 8) {
          // Not a float64 - unexpected format, bail out safely
          return blob;
        }

        // Write actual duration as IEEE 754 double
        const copy = new Uint8Array(buffer);
        const view = new DataView(copy.buffer);
        view.setFloat64(durValueOffset, durationMs, false); // big-endian

        return new Blob([copy], { type: blob.type });
      } catch (err) {
        console.warn(
          '[fixWebmDuration] Could not fix duration metadata, using original blob:',
          err,
        );
        return blob;
      }
    },
    [],
  );

  const playSound = useCallback((soundFile: string) => {
    try {
      const audio = new Audio(`/audios/${soundFile}`);
      audio.play().catch(err => {
        console.warn('Failed to play recording sound:', err);
      });
    } catch (err) {
      console.error('Error initializing audio:', err);
    }
  }, []);

  const logRecordEvent = useCallback(
    async (
      unitId: string | number,
      eventType: string,
      errorMessage?: string,
      metadata?: any,
    ) => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        await fetch(`${API_SERVICE_URL}/feature-tracking`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            featureName: 'screen_recording',
            userId: userId ? Number(userId) : undefined,
            eventType,
            sessionId: String(unitId),
            metadata: {
              ...metadata,
              errorMessage,
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString(),
            },
          }),
        });
      } catch (err) {
        console.warn('[ScreenRecorder] Failed to log event:', eventType, err);
      }
    },
    [userId],
  );

  const stopRecording = useCallback(() => {
    setIsConfirmStopOpen(false);
    // Check ref immediately for the most up-to-date status
    if (recordingStatusRef.current === 'recording') {
      // 1. Play stop sound immediately
      playSound('stop-recording.mp3');

      const currentActiveSessionId = activeSessionIdRef.current;
      if (currentActiveSessionId) {
        void logRecordEvent(
          currentActiveSessionId,
          'recording_stopped',
          undefined,
          {
            reason: 'manually_clicked',
            courseId: sessionInfoRef.current?.courseId,
            classType: sessionInfoRef.current?.title.includes('[Trial/Test]')
              ? 'trial_test'
              : 'main_class',
          },
        );
      }
    }

    // 1. Stop MediaRecorder - Request data first to ensure all chunks are captured
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      try {
        // Request any remaining data before stopping
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.requestData();
        }
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.error('Error stopping recorder:', err);
      }
      mediaRecorderRef.current = null;
    }

    // 2. Stop all tracks in screenStream (Crucial to remove "Sharing" bar)
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      screenStreamRef.current = null;
    }

    // 3. Stop all tracks in micStream
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      micStreamRef.current = null;
    }

    // 4. Stop any tracks in the combined final stream
    if (finalStreamRef.current) {
      finalStreamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      finalStreamRef.current = null;
    }

    // 5. Close AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }

    setRecordingStatus('idle');
    recordingStatusRef.current = 'idle';
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    sessionInfoRef.current = null;
  }, [playSound, logRecordEvent]);
  // Note: We keep recordingStatus and activeSessionId in deps for now to be safe,
  // but the logic above handles the 'immediate stop' by checking current values.
  // To truly fix the dependency chain, we'll refactor the cleanup effect.

  const startRecording = async (
    sessionId: string,
    courseId: string,
    subject: string,
    title: string,
  ) => {
    if (isStartingRef.current || recordingStatus === 'recording') return;
    isStartingRef.current = true;

    try {
      // Clear previous recording to prevent modal from showing up during recording
      setRecordedVideo(null);

      // Play start sound - MOVED to after screen selection
      // playSound('start-recording.mp3');

      // 1. Get Screen Stream (Video + System Audio)
      void logRecordEvent(sessionId, 'permission_requested', undefined, {
        courseId,
        classType: title.includes('[Trial/Test]') ? 'trial_test' : 'main_class',
      });

      const screenStream = await (navigator.mediaDevices as any).getDisplayMedia(
        {
          video: {
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 20, max: 24 }, // Reduced FPS for sharper text per frame
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        },
      );

      // Verify system audio is shared (required for recording)
      const videoTrack = screenStream.getVideoTracks()[0];
      const audioTrackCount = screenStream.getAudioTracks().length;
      const settings = videoTrack?.getSettings() as any;
      const displaySurface = settings?.displaySurface;

      if (audioTrackCount === 0) {
        void logRecordEvent(
          sessionId,
          'permission_denied',
          'No system audio shared',
          {
            courseId,
            classType: title.includes('[Trial/Test]')
              ? 'trial_test'
              : 'main_class',
            displaySurface,
            hasSystemAudio: false,
          },
        );

        // Stop all tracks to clear the sharing bar
        screenStream
          .getTracks()
          .forEach((track: MediaStreamTrack) => track.stop());

        // Play stop sound immediately to alert user
        playSound('stop-recording.mp3');

        const errorMsg = intl.formatMessage({
          id: 'screen_recorder.error.no_audio',
          defaultMessage:
            'Please check "ALSO SHARE SYSTEM AUDIO" box to record.',
        });

        notification.error({
          title: intl.formatMessage({
            id: 'screen_recorder.error.title',
            defaultMessage: 'Recording Permission Restricted',
          }),
          text: errorMsg,
        });

        // Use window.alert to force the browser to jump back to this tab
        setTimeout(() => {
          window.alert(errorMsg);
        }, 100);
        return;
      }

      void logRecordEvent(sessionId, 'permission_granted', undefined, {
        courseId,
        classType: title.includes('[Trial/Test]') ? 'trial_test' : 'main_class',
        displaySurface,
        hasSystemAudio: audioTrackCount > 0,
        browserWidth: window.innerWidth,
        browserHeight: window.innerHeight,
      });

      screenStreamRef.current = screenStream;

      // 2. Get Microphone Stream (Teacher's voice)
      let micStream: MediaStream | null = null;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        micStreamRef.current = micStream;
      } catch (micErr) {
        console.warn(
          'Microphone access denied, proceeding with system audio only.',
          micErr,
        );
        notification.warning({
          title:
            'Microphone not found or denied. Your voice will not be recorded.',
        });
      }

      // 3. Mix System Audio + Mic Audio if both exist
      let finalStream = screenStream;

      const hasSystemAudio = screenStream.getAudioTracks().length > 0;

      if (micStream && hasSystemAudio) {
        const context = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        audioContextRef.current = context;

        const systemSource = context.createMediaStreamSource(screenStream);
        const micSource = context.createMediaStreamSource(micStream);

        const destination = context.createMediaStreamDestination();

        systemSource.connect(destination);
        micSource.connect(destination);

        // Ensure AudioContext is running
        if (context.state === 'suspended') {
          await context.resume();
        }

        finalStream = new MediaStream([
          ...screenStream.getVideoTracks(),
          ...destination.stream.getAudioTracks(),
        ]);
      } else if (micStream) {
        finalStream = new MediaStream([
          ...screenStream.getVideoTracks(),
          ...micStream.getAudioTracks(),
        ]);
      }

      finalStreamRef.current = finalStream;
      chunksRef.current = [];

      const getMimeType = () => {
        const types = [
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp8,opus',
          'video/webm',
        ];
        for (const type of types) {
          if ((window as any).MediaRecorder.isTypeSupported(type)) {
            return type;
          }
        }
        return 'video/webm';
      };

      const selectedMimeType = getMimeType();
      mimeTypeRef.current = selectedMimeType;

      const options = {
        mimeType: selectedMimeType,
        // User Requested: 2.0 Mbps - Sweet spot for text clarity vs size
        videoBitsPerSecond: 2000000,
        audioBitsPerSecond: 128000,
      };

      const recorder = new (window as any).MediaRecorder(finalStream, options);

      recorder.ondataavailable = (e: any) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const rawBlob = new Blob(chunksRef.current, { type: selectedMimeType });

        // Fix WebM duration metadata so the downloaded file is seekable
        const durationMs = recordingStartTimeRef.current
          ? Date.now() - recordingStartTimeRef.current
          : 0;
        const blob =
          durationMs > 0 ? await fixWebmDuration(rawBlob, durationMs) : rawBlob;

        const url = URL.createObjectURL(blob);
        const timestamp = Date.now();
        const videoId = `video_${sessionId}_${timestamp}`;

        // Auto-download the recording to user's machine immediately after stop
        try {
          const dateStr = new Date().toISOString().slice(0, 10);
          const safeTitle = title.replace(/[^a-z0-9]/gi, '_') || 'Session';
          const downloadFileName = `Antoree_Recording_${safeTitle}_${dateStr}.webm`;
          const a = document.createElement('a');
          a.href = url;
          a.download = downloadFileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (dlErr) {
          console.warn('[ScreenRecorder] Auto-download failed:', dlErr);
        }

        const recordedVideoData: RecordedVideo = {
          blob,
          url,
          sessionId,
          courseId,
          sessionTitle: title,
          timestamp,
          id: videoId,
          uploadStatus: 'pending',
        };

        // Save to IndexedDB as backup
        try {
          await videoStorage.saveVideo({
            id: videoId,
            blob,
            sessionId,
            courseId,
            sessionTitle: title,
            timestamp,
            uploadStatus: 'pending',
            uploadAttempts: 0,
          });
        } catch (err) {
          console.error(
            '[ScreenRecorder] Failed to save video to IndexedDB:',
            err,
          );
          // Still show the video even if IndexedDB save fails
        }

        setRecordedVideo(recordedVideoData);

        void logRecordEvent(sessionId, 'recording_saved', undefined, {
          courseId,
          fileSize: blob.size,
          durationMs,
          classType: title.includes('[Trial/Test]')
            ? 'trial_test'
            : 'main_class',
        });

        chunksRef.current = []; // Clear chunks after creating blob
      };

      recorder.onerror = (event: any) => {
        console.error('MediaRecorder error:', event);
        notification.error({
          title: 'Recording error occurred',
          text: 'Your recording may have been interrupted. Please try again.',
        });

        void logRecordEvent(
          sessionId,
          'recording_failed',
          event.error?.message || 'MediaRecorder error',
          {
            courseId,
            classType: title.includes('[Trial/Test]')
              ? 'trial_test'
              : 'main_class',
            errorName: event.error?.name,
          },
        );

        stopRecording();
      };

      mediaRecorderRef.current = recorder;
      // Track start time to calculate accurate duration for WebM metadata fix
      recordingStartTimeRef.current = Date.now();
      // Balanced timeslice: 2s = good balance between data safety and performance
      // 1s = safest but more I/O, 5s = less I/O but risk losing 5s of data
      recorder.start(2000);

      // Play start sound AFTER user has selected screen and recording actually starts
      playSound('start-recording.mp3');

      void logRecordEvent(sessionId, 'recording_started', undefined, {
        courseId,
        mimeType: selectedMimeType,
        videoBitsPerSecond: options.videoBitsPerSecond,
        audioBitsPerSecond: options.audioBitsPerSecond,
        classType: title.includes('[Trial/Test]') ? 'trial_test' : 'main_class',
      });

      setRecordingStatus('recording');
      recordingStatusRef.current = 'recording';
      setActiveSessionId(sessionId);
      activeSessionIdRef.current = sessionId;

      // Show warning about native stop sharing button using custom modal
      setIsShowStartWarningModal(true);

      // Store session info
      sessionInfoRef.current = { sessionId, courseId, title };

      // Open Antoree Call page with access token after starting record
      // const token = localStorage.getItem('token');
      // if (token) {
      //   window.open(`https://call.antoree.com?accessToken=${token}`, '_blank');
      // }

      // Call API to record the start time
      try {
        const recordedAt = new Date().toISOString();
        await fetch(
          `${API_SERVICE_URL}/student/course-units/${sessionId}/recorded-at`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ recordedAt }),
          },
        );
      } catch (apiErr) {
        console.error('Failed to save recording start time:', apiErr);
        // Don't stop recording if API call fails, just log the error
      }

      // Handle termination from browser share bar
      const vt = screenStream.getVideoTracks()[0];
      if (vt) {
        vt.onended = () => {
          void logRecordEvent(sessionId, 'recording_stopped', undefined, {
            reason: 'browser_share_bar_stopped',
            courseId,
            classType: title.includes('[Trial/Test]')
              ? 'trial_test'
              : 'main_class',
          });

          const warningTitle = intl.formatMessage({
            id: 'screen_recorder.warning.stop_sharing_title',
            defaultMessage: 'Screen sharing stopped - Recording turned off',
          });
          const warningText = intl.formatMessage({
            id: 'screen_recorder.warning.stop_sharing_text',
            defaultMessage:
              'Warning: You just clicked "Stop sharing" on the browser toolbar. The recording session has ended and the video is being saved!',
          });

          notification.warning({
            title: warningTitle,
            text: warningText,
          });

          setTimeout(() => {
            try {
              window.alert(`${warningTitle}\n\n${warningText}`);
            } catch (e) {}
          }, 100);

          stopRecording();
        };
      }
    } catch (err: any) {
      console.error('Recording error details:', err);
      let errorMsg = 'Could not start screen recording.';
      if (err.name === 'NotAllowedError') {
        errorMsg = 'Screen access permission denied or cancelled by user.';
      } else if (
        err.name === 'SecurityError' ||
        err.message.includes('permissions policy')
      ) {
        errorMsg =
          'Browser or environment is blocking screen recording. Please check browser settings.';
      }
      notification.error({ title: errorMsg });

      void logRecordEvent(
        sessionId,
        err.name === 'NotAllowedError' ? 'permission_denied' : 'browser_error',
        err.message,
        {
          courseId,
          classType: title.includes('[Trial/Test]')
            ? 'trial_test'
            : 'main_class',
          errorName: err.name,
        },
      );

      stopRecording();
    } finally {
      isStartingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      // Stop recording if provider unmounts while recording is active
      if (recordingStatusRef.current === 'recording') {
        stopRecording();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({
      recordingStatus,
      recordedVideo,
      startRecording,
      stopRecording,
      requestStopRecording,
      cancelStopRecording,
      isConfirmStopOpen,
      isShowStartWarningModal,
      closeStartWarningModal,
      setRecordedVideo,
      activeSessionId,
      setAiTip: () => {},
    }),
    [
      recordingStatus,
      recordedVideo,
      startRecording,
      stopRecording,
      requestStopRecording,
      cancelStopRecording,
      isConfirmStopOpen,
      isShowStartWarningModal,
      closeStartWarningModal,
      activeSessionId,
    ],
  );

  return (
    <ScreenRecorderContext.Provider value={contextValue}>
      {children}
    </ScreenRecorderContext.Provider>
  );
};

export const useScreenRecorder = () => {
  const context = useContext(ScreenRecorderContext);
  if (context === undefined) {
    throw new Error(
      'useScreenRecorder must be used within a ScreenRecorderProvider',
    );
  }
  return context;
};
